import React, { useState, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currencyUtils';
import { formatDate } from '../utils/dateUtils';

const ExpensesTab = ({ propertyId, expenses = [], onExpenseUpdate }) => {
  const { agencyCurrency, user, agencyCountry } = useContext(AuthContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Helper function for consistent date formatting
  const formatDateWithContext = (dateString) => {
    return formatDate(dateString, {
      userPreference: user?.dateFormatPreference,
      agencyCountry: agencyCountry
    });
  };

  const expenseCategories = [
    'Maintenance & Repairs',
    'Utilities',
    'Insurance',
    'Property Tax',
    'Cleaning',
    'Landscaping',
    'Security',
    'Legal & Professional',
    'Marketing',
    'Supplies',
    'Other'
  ];

  // Filter and sort expenses
  const filteredExpenses = expenses
    .filter(expense => {
      const matchesSearch = expense.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          expense.vendor?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || expense.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'date':
          comparison = new Date(a.date) - new Date(b.date);
          break;
        case 'amount':
          comparison = a.amount - b.amount;
          break;
        case 'description':
          comparison = a.description.localeCompare(b.description);
          break;
        case 'category':
          comparison = a.category.localeCompare(b.category);
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'desc' ? -comparison : comparison;
    });

  // Calculate totals
  const totalExpenses = filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);
  const monthlyExpenses = filteredExpenses
    .filter(expense => {
      const expenseDate = new Date(expense.date);
      const currentDate = new Date();
      return expenseDate.getMonth() === currentDate.getMonth() &&
             expenseDate.getFullYear() === currentDate.getFullYear();
    })
    .reduce((sum, expense) => sum + expense.amount, 0);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return 'bi-arrow-down-up';
    return sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
  };


  const getCategoryColor = (category) => {
    const colors = {
      'Maintenance & Repairs': 'danger',
      'Utilities': 'primary',
      'Insurance': 'info',
      'Property Tax': 'warning',
      'Cleaning': 'success',
      'Landscaping': 'success',
      'Security': 'dark',
      'Legal & Professional': 'secondary',
      'Marketing': 'purple',
      'Supplies': 'info',
      'Other': 'secondary'
    };
    return colors[category] || 'secondary';
  };

  return (
    <div className="expenses-tab">
      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-danger bg-opacity-10 rounded-3 p-3">
                    <i className="bi bi-receipt text-danger fs-4"></i>
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h6 className="mb-1">Total Expenses</h6>
                  <h4 className="text-danger mb-0">{formatCurrency(totalExpenses, agencyCurrency)}</h4>
                  <small className="text-muted">{filteredExpenses.length} expenses</small>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-warning bg-opacity-10 rounded-3 p-3">
                    <i className="bi bi-calendar-month text-warning fs-4"></i>
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h6 className="mb-1">This Month</h6>
                  <h4 className="text-warning mb-0">{formatCurrency(monthlyExpenses, agencyCurrency)}</h4>
                  <small className="text-muted">
                    {filteredExpenses.filter(e => {
                      const expenseDate = new Date(e.date);
                      const currentDate = new Date();
                      return expenseDate.getMonth() === currentDate.getMonth() &&
                             expenseDate.getFullYear() === currentDate.getFullYear();
                    }).length} expenses
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Search Expenses</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by description or vendor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Category</label>
              <select
                className="form-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                {expenseCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Sort By</label>
              <select
                className="form-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="date">Date</option>
                <option value="amount">Amount</option>
                <option value="description">Description</option>
                <option value="category">Category</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Expenses List */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white border-0">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="bi bi-receipt me-2"></i>
              Expenses ({filteredExpenses.length})
            </h6>
            <small className="text-muted">
              Total: {formatCurrency(totalExpenses, agencyCurrency)}
            </small>
          </div>
        </div>
        <div className="card-body p-0">
          {filteredExpenses.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-receipt display-1 text-muted"></i>
              <h5 className="text-muted mt-3">No expenses found</h5>
              <p className="text-muted">
                {expenses.length === 0
                  ? "No expenses have been recorded for this property yet."
                  : "Try adjusting your search or filter criteria."
                }
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th
                      className="cursor-pointer"
                      onClick={() => handleSort('date')}
                    >
                      Date <i className={`bi ${getSortIcon('date')}`}></i>
                    </th>
                    <th
                      className="cursor-pointer"
                      onClick={() => handleSort('description')}
                    >
                      Description <i className={`bi ${getSortIcon('description')}`}></i>
                    </th>
                    <th
                      className="cursor-pointer"
                      onClick={() => handleSort('category')}
                    >
                      Category <i className={`bi ${getSortIcon('category')}`}></i>
                    </th>
                    <th
                      className="cursor-pointer text-end"
                      onClick={() => handleSort('amount')}
                    >
                      Amount <i className={`bi ${getSortIcon('amount')}`}></i>
                    </th>
                    <th>Vendor</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.map(expense => (
                    <tr key={expense.id}>
                      <td>
                        <small className="text-muted">{formatDateWithContext(expense.date)}</small>
                      </td>
                      <td>
                        <div>
                          <div className="fw-medium">{expense.description}</div>
                          {expense.notes && (
                            <small className="text-muted">{expense.notes}</small>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`badge bg-${getCategoryColor(expense.category)} bg-opacity-10 text-${getCategoryColor(expense.category)}`}>
                          {expense.category}
                        </span>
                      </td>
                      <td className="text-end">
                        <span className="fw-medium text-danger">
                          {formatCurrency(expense.amount, agencyCurrency)}
                        </span>
                      </td>
                      <td>
                        <span className="text-muted">{expense.vendor || '-'}</span>
                      </td>
                      <td className="text-center">
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => onExpenseUpdate('edit', expense)}
                            title="Edit expense"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => onExpenseUpdate('delete', expense)}
                            title="Delete expense"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpensesTab;
